import { ExpressionSpecification, FilterSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import {
  COLOR_GREEN_400,
  COLOR_RED_300,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_PRIMARY_INVERSE,
  COLOR_TEXT_SECONDARY,
  COLOR_YELLOW_300,
  FONT_SIZE_100
} from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import { buildDynamicTrailPinProps, buildRegularTrailIconImage } from '../../utils/buildPinIconExpressions';
import { getLayerBeforeId, regularTrailPinsId } from '../../utils/layers';
import { dynamicPinMarkerVerticalOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import {
  isTrailClosedExpression,
  isTrailCollapsedExpression,
  isTrailHasPriorityExpression,
  isTrailPriorityHighExpression,
  isTrailPriorityLowExpression,
  isTrailPriorityMediumExpression
} from '../../utils/trailPinConstants';

type RegularTrailPinLayerProps = {
  sourceId: string;
  isHoveredExpression: ExpressionSpecification;
  isSelectedExpression: ExpressionSpecification;
  isViewedExpression: ExpressionSpecification;
  isActiveTrailExpression: ExpressionSpecification;
};

const RegularTrailPinLayer = ({
  sourceId,
  isHoveredExpression,
  isSelectedExpression,
  isViewedExpression,
  isActiveTrailExpression
}: RegularTrailPinLayerProps) => {
  const map = useMap();
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];
  const trailIconImage = buildRegularTrailIconImage(isHoveredExpression, isSelectedExpression, isViewedExpression, isActiveTrailExpression);
  const pinLayoutProps = buildDynamicTrailPinProps(isHoveredExpression, isSelectedExpression, isActiveTrailExpression);

  return (
    <>
      <Layer
        source={sourceId}
        type="symbol"
        id={regularTrailPinsId}
        beforeId={getLayerBeforeId(map, regularTrailPinsId)}
        filter={['all', ['!', ['has', 'point_count']], ['!', isTrailCollapsedExpression]] as FilterSpecification}
        layout={{
          'icon-image': trailIconImage,
          ...pinLayoutProps,
          'text-offset': [0, (-1 * dynamicPinMarkerVerticalOffset) / FONT_SIZE_100]
        }}
        paint={{
          'text-color': [
            'case',
            ['all', isActiveOrHovered, isTrailPriorityHighExpression],
            COLOR_RED_300,
            ['all', isActiveOrHovered, isTrailPriorityMediumExpression],
            COLOR_YELLOW_300,
            ['all', isActiveOrHovered, isTrailPriorityLowExpression],
            COLOR_GREEN_400,
            isActiveOrHovered,
            COLOR_TEXT_PRIMARY_INVERSE, // White text on active/hover (green background)
            // Note: saved/completed/closed pins now have white background in default state,
            // so they need dark text, not white
            isTrailHasPriorityExpression, // Priority takes precedence over closed
            COLOR_TEXT_PRIMARY,
            isTrailClosedExpression,
            COLOR_TEXT_SECONDARY, // Dark text on closed (light gray background)
            COLOR_TEXT_PRIMARY // Default dark text (for trail, saved, completed in default state)
          ]
        }}
      />
    </>
  );
};

export default RegularTrailPinLayer;
